import {
  wppTags,
  wppTextFormat,
} from '@/components/Movements/WorkOrders/Details/WorkOrderFooter/ComplementWhatsappButton/options';
import { FloatingFormItem, Group } from '@/components/_common';
import TextareaVars from '@/components/_common/TextareaVars';
import {
  useMatrixWhatsappSettingsCreate,
  useMatrixWhatsappSettingsUpdate,
} from '@/core/domain/enterprise/MatrixWhatsappSettings/MatrixWhatsappSettings.hooks';
import {
  MatrixWhatsappSettings,
  MatrixWhatsappSettingsCreate,
} from '@/core/domain/enterprise/MatrixWhatsappSettings/MatrixWhatsappSettings.types';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Modal,
  ModalProps,
  Popover,
  Typography,
} from 'antd';
import React, { useCallback, useEffect } from 'react';
import {
  complementMock,
  workOrderMock,
  workorderItemsMock,
} from '@/core/domain/enterprise/MatrixWhatsappSettings/Mock';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { MatrixPartnerOrigin } from '@/types/Register';

const { Text } = Typography;

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: VoidFunction;
  data?: MatrixWhatsappSettings;
};

const WhatsappTemplatesModal: React.FC<Props> = ({
  open,
  data,
  onCancel,
  ...props
}) => {
  const [form] = Form.useForm<MatrixWhatsappSettingsCreate>();
  const message = Form.useWatch('message', form);

  const { data: matrix } = useMatrix();

  const filterTagsForBosch = useCallback(
    (tag: string) => {
      const isBoschOrigin =
        !!matrix?.partnerOrigin &&
        [
          MatrixPartnerOrigin.BOSCH_CAR_SERVICE,
          MatrixPartnerOrigin.BOSCH_MODULO,
        ].includes(matrix?.partnerOrigin);

      if (!isBoschOrigin && tag === '{{boschCoReURL}}') return false;

      return true;
    },
    [matrix?.partnerOrigin],
  );

  const tags = Object.keys(wppTags)
    .filter(filterTagsForBosch)
    .map((tag) => ({
      value: tag,
      label: wppTags[tag],
    }));

  const { mutateAsync: create, isLoading: loadingCreate } =
    useMatrixWhatsappSettingsCreate();
  const { mutateAsync: update, isLoading: loadingUpdate } =
    useMatrixWhatsappSettingsUpdate();

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSave = async () => {
    const values = await form.validateFields();

    if (data?.id) {
      await update({ id: data.id, ...values });
    } else {
      await create(values);
    }

    handleCancel();
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data });
    }
  }, [open]);

  return (
    <Modal
      {...props}
      title={`${data?.id ? 'Editar' : 'Criar'} Template Whatsapp`}
      open={open}
      width={800}
      onOk={handleSave}
      okText="Salvar"
      confirmLoading={loadingUpdate || loadingCreate}
      onCancel={handleCancel}
    >
      <Form form={form}>
        <Group position="between">
          <Text>Template da mensagem:</Text>

          <Popover
            content={
              <div style={{ width: 400 }}>
                {tags.map((tag) => (
                  <div key={tag.value} style={{ marginBottom: 16 }}>
                    <Typography.Text>{`${tag.value}: ${tag.label}`}</Typography.Text>
                  </div>
                ))}
              </div>
            }
            placement="bottomRight"
          >
            <Button type="link">Tags</Button>
          </Popover>
        </Group>

        <Text>
          Colocar textos entre (*) deixará o texto em negrito no WhatsApp.
        </Text>

        <Divider />

        <FloatingFormItem
          name="title"
          label="Título"
          rules={[{ required: true }]}
        >
          <Input />
        </FloatingFormItem>

        <Form.Item name="message" noStyle rules={[{ required: true }]}>
          <TextareaVars tags={tags} rows={10} maxLength={500} showCount />
        </Form.Item>

        {message && (
          <>
            <Divider />

            <Alert
              type="info"
              description={
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    wordWrap: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: `${wppTextFormat({
                      wppText: message,
                      workorder: workOrderMock,
                      complement: complementMock,
                      workorderItems: workorderItemsMock,
                      checklistImages: [
                        'http://exemplo.com/url/da/imagem001',
                        'http://exemplo.com/url/da/imagem002',
                      ],
                    })}`,
                  }}
                />
              }
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default WhatsappTemplatesModal;
